import type { CalculatorResultaat } from './berekenAdvies';
import type { ZwareVerbruikers } from './StapLaadvoorkeur';
import { trackAssessmentCtaClicked } from '@shared/analytics';

interface Props {
  resultaat: CalculatorResultaat;
  assessmentUrl: string;
  ajaxUrl: string;
  nonce: string;
  zwareVerbruikers: ZwareVerbruikers;
  beschikbareUren: number;
  onTerug: () => void;
}

export default function StapResultaat({ resultaat, assessmentUrl, onTerug, zwareVerbruikers, beschikbareUren }: Props) {
  const {
    aanbevolenKw,
    adviesType,
    dagelijksVerbruikKwh,
    laadtijdUur,
    laadtijdMin,
    kmPerUurLaden,
    maandkostenThuis,
    maandkostenPubliek,
    besparing,
  } = resultaat;

  const berekendeKw = (dagelijksVerbruikKwh / beschikbareUren).toFixed(1).replace('.', ',');
  const dagelijksVerbruikKwhFormatted = dagelijksVerbruikKwh.toFixed(1).replace('.', ',');

  const aantalZwareVerbruikers = Object.values(zwareVerbruikers).filter(Boolean).length;

  function formatLaadtijd(): string {
    if (laadtijdUur === 0) return `${laadtijdMin} min`;
    if (laadtijdMin === 0) return `${laadtijdUur} uur`;
    return `${laadtijdUur} uur ${laadtijdMin} min`;
  }

  return (
    <div className="lc-stap lc-stap--resultaat">
      <h2 className="lc-stap__titel">Jouw laadadvies</h2>

      {/* Recommended power — hero */}
      <div className="lc-resultaat-hero">
        <div className="lc-resultaat-hero__vermogen">
          <span className="lc-resultaat-hero__getal">{aanbevolenKw}</span>
          <span className="lc-resultaat-hero__eenheid"> kW</span>
        </div>
        <p className="lc-resultaat-hero__omschrijving">aanbevolen laadvermogen</p>
      </div>

      {/* Calculation explanation */}
      <p className="lc-uitleg-tekst">
        Je hebt ongeveer {dagelijksVerbruikKwhFormatted} kWh per dag nodig. Met {beschikbareUren} uur laadtijd is dat {berekendeKw} kW — we raden {aanbevolenKw} kW aan voor een comfortabele marge.
      </p>

      {/* Stats grid */}
      <div className="lc-stats-grid">
        <div className="lc-stat">
          <span className="lc-stat__waarde">{formatLaadtijd()}</span>
          <span className="lc-stat__label">Laadtijd per dag</span>
        </div>
        <div className="lc-stat">
          <span className="lc-stat__waarde">{kmPerUurLaden} km/uur</span>
          <span className="lc-stat__label">Bereik per uur laden</span>
        </div>
        <div className="lc-stat">
          <span className="lc-stat__waarde">€{maandkostenThuis}/mnd</span>
          <span className="lc-stat__label">Geschatte kosten thuis</span>
        </div>
        <div className="lc-stat lc-stat--highlight">
          <span className="lc-stat__waarde">€{besparing}/mnd</span>
          <span className="lc-stat__label">Besparing t.o.v. publiek laden</span>
        </div>
      </div>

      {/* Comparison bar */}
      <div className="lc-vergelijking">
        <div className="lc-vergelijking__rij">
          <span className="lc-vergelijking__label">Thuis laden</span>
          <span className="lc-vergelijking__prijs lc-vergelijking__prijs--thuis">
            €{maandkostenThuis}/maand
          </span>
        </div>
        <div className="lc-vergelijking__rij">
          <span className="lc-vergelijking__label">Publiek laden</span>
          <span className="lc-vergelijking__prijs lc-vergelijking__prijs--publiek">
            €{maandkostenPubliek}/maand
          </span>
        </div>
      </div>

      {/* CTA — primary */}
      <div className="lc-cta-blok">
        {aantalZwareVerbruikers >= 2 && (
          <p className="lc-cta-blok__zware-verbruikers">
            Je hebt meerdere zware verbruikers. Dat vergroot de kans op overbelasting. Een
            meterkastcheck is sterk aanbevolen.
          </p>
        )}
        <p className="lc-cta-blok__vraag">
          {adviesType === 'geschikt'
            ? 'Wil je zeker weten dat je thuislaadinstallatie klopt?'
            : adviesType === 'nader_onderzoek'
            ? `Wil je weten of jouw meterkast een ${aanbevolenKw} kW laadpaal aankan?`
            : `${aanbevolenKw} kW vereist bijna altijd een meterkastcheck. Wil je dit laten beoordelen?`}
        </p>
        <a
          href={assessmentUrl}
          className="lc-btn lc-btn--cta"
          onClick={() => trackAssessmentCtaClicked(adviesType)}
        >
          Start Meterkast Assessment — €29,95
        </a>
        <p className="lc-cta-blok__sub">
          Krijg binnen 24 uur een persoonlijk rapport op basis van jouw situatie.
        </p>
      </div>

      {/* Back link */}
      <div className="lc-navigatie lc-navigatie--resultaat">
        <button type="button" className="lc-btn lc-btn--link" onClick={onTerug}>
          ← Antwoorden aanpassen
        </button>
      </div>
    </div>
  );
}
